import { RadioChangeEvent, Space } from 'antd';
import {
  Button,
  Table,
  Paging,
  usePaging,
  EllipsisSpan,
  useUpdate,
  PopConfirm,
  Radio,
  Tag,
  Checkbox,
  message,
  Modal,
} from '@maxtropy/components';
import { FC, useEffect, useState } from 'react';
import styles from '../index.module.scss';
import { DelayTitleEnum, ApprovalStatusDisplay, ApprovalStatus, ColorDisplay } from '../utils';
import { ApprovalStatus as BatchApprovalStatus } from '@/pages/DeferredApplicationApproval/util';
import { SearchProps } from '..';
import dayjs from 'dayjs';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  apiV2EnergyConsumptionPlanResubmitPost,
  apiV2EnergyConsumptionPlanRevokePost,
  apiV2EnergyConsumptionPlanDeleteDelayEnergyConsumptionPost,
  apiV2EnergyConsumptionPlanGetNumOfEachApprovalStatusPost,
  V2EnergyConsumptionPlanGetNumOfEachApprovalStatusPostResponse,
  V2EnergyConsumptionPlanGetDelayEnergyConsumptionPagePostResponse,
  apiV2EnergyConsumptionPlanGetDelayEnergyConsumptionPagePost,
  apiV2EnergyConsumptionPlanOneKeyApprovalPost,
  apiV2H5EnergyPlanEncourageCompletionPost,
} from '@maxtropy/device-customer-apis-v2';
import { isEmpty } from 'lodash-es';
import { PlusOutlined } from '@ant-design/icons';
import { PermissionsType } from '@/common/permissionsConst';
import { useHasPermission } from '@/utils/utils';

export interface DelayProps {
  searchParams: SearchProps;
}
type DataItem = Exclude<V2EnergyConsumptionPlanGetDelayEnergyConsumptionPagePostResponse['list'], undefined>[number];

const columns = [
  {
    title: '部门',
    dataIndex: 'ownerOrgName',
    width: 140,
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '区域',
    dataIndex: 'areaList',
    width: 200,
    ellipsis: { showTitle: true },
    render: (v: DataItem['areaList']) => (
      <EllipsisSpan value={isEmpty(v) ? undefined : v!.map((item: any) => item?.zoneName).join('、')} />
    ),
  },
  {
    title: '申请状态',
    dataIndex: 'approvalResult',
    width: 180,
    ellipsis: { showTitle: true },
    render: (v: ApprovalStatus) => (
      <Tag border="solid" color={ColorDisplay[v]}>
        {ApprovalStatusDisplay[v]}
      </Tag>
    ),
  },
  {
    title: '申请人',
    dataIndex: 'applyUserName',
    width: 140,
    ellipsis: { showTitle: true },
    render: (value: string) => {
      return <EllipsisSpan value={value} />;
    },
  },
  {
    title: '申请时间范围',
    dataIndex: 'timeRange',
    width: 200,
    ellipsis: { showTitle: true },
    render: (value: string) => {
      return <EllipsisSpan value={value} />;
    },
  },
  {
    title: '申请能源介质',
    dataIndex: 'energyMedium',
    width: 200,
    ellipsis: { showTitle: true },
    render: (value: string) => {
      return <EllipsisSpan value={value} />;
    },
  },
  {
    title: '申请时间',
    dataIndex: 'applyTime',
    width: 200,
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v && dayjs(v).format('YYYY-MM-DD HH:mm:ss')} />,
  },
];

const DelayApplication: FC<DelayProps> = props => {
  const { searchParams } = props;
  const pagingInfo = usePaging(20);
  const { pageOffset, pageSize, setTotalCount } = pagingInfo;
  const navigate = useNavigate();
  const [grade, setGrade] = useState(DelayTitleEnum.ALL);
  const [check, setCheck] = useState<boolean>();
  const [delayData, setDelayData] = useState<DataItem[]>([]);
  const [stateDatas, setStateDatas] = useState<V2EnergyConsumptionPlanGetNumOfEachApprovalStatusPostResponse>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [updateState, updateFn] = useUpdate();
  const [urlSearchParams] = useSearchParams();

  // 一键审批相关状态
  const [isBatchApprovalMode, setIsBatchApprovalMode] = useState<boolean>(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [batchApprovalLoading, setBatchApprovalLoading] = useState<boolean>(false);

  const hasApplicationForDelay = useHasPermission(PermissionsType.B_APPLICATIONFORDELAY);

  useEffect(() => {
    const { time, ...params } = searchParams;
    setLoading(true);

    apiV2EnergyConsumptionPlanGetDelayEnergyConsumptionPagePost({
      ...params,
      todoStatus: check ? 0 : undefined,
      page: pageOffset,
      size: pageSize,
      status: grade ?? null,
      startTime: searchParams?.time ? dayjs(searchParams?.time[0]).startOf('day').valueOf() : undefined,
      endTime: searchParams?.time ? dayjs(searchParams?.time[1]).endOf('day').valueOf() : undefined,
    })
      .then(res => {
        setDelayData(res.list ?? []);
        setTotalCount(res?.total ?? 0);
      })
      .finally(() => setLoading(false));
    apiV2EnergyConsumptionPlanGetNumOfEachApprovalStatusPost({
      ...params,
      // 数量实际上是查询的所有，只是后端用了同一个对象，所以让传page和size
      page: pageOffset,
      size: pageSize,
      todoStatus: check ? 0 : undefined,
      startTime: searchParams?.time ? dayjs(searchParams?.time[0]).startOf('day').valueOf() : undefined,
      endTime: searchParams?.time ? dayjs(searchParams?.time[1]).endOf('day').valueOf() : undefined,
    }).then(res => {
      setStateDatas(res);
    });
  }, [searchParams, updateState, pageOffset, pageSize, setTotalCount, grade, check]);

  const onRelaunched = (val: number) => {
    apiV2EnergyConsumptionPlanResubmitPost({ id: val }).then(res => {
      updateFn();
    });
  };
  const onEncourageCompletion = (val: number) => {
    apiV2H5EnergyPlanEncourageCompletionPost({ id: val }).then(res => {
      updateFn();
    });
  };

  const onRecall = (val: number) => {
    apiV2EnergyConsumptionPlanRevokePost({ id: val }).then(res => {
      updateFn();
    });
  };

  const buildColumns = [
    ...columns,
    {
      title: '操作',
      dataIndex: 'operation',
      width: 220,
      fixed: 'right' as const,
      render: (value: undefined, record: any) => {
        const status = record.approvalResult;
        return (
          <Space size={16}>
            <Button
              type="link"
              onClick={() =>
                navigate(`/energy/usage/application/deferred/detail/${record.id}?${urlSearchParams.toString()}`)
              }
            >
              查看
            </Button>
            {record.copyable && (
              <Button
                type="link"
                onClick={() =>
                  navigate(`/energy/usage/application/deferred?copyId=${record.id}&${urlSearchParams.toString()}`)
                }
              >
                复制
              </Button>
            )}
            {record.approvable &&
              (status === ApprovalStatus.PENDING_FIRST || status === ApprovalStatus.PENDING_SECOND) && (
                <Button
                  type="link"
                  onClick={() =>
                    navigate(`/energy/usage/application/approval/${record.id}?${urlSearchParams.toString()}`)
                  }
                >
                  审批
                </Button>
              )}
            {record.editable && status === ApprovalStatus.PENDING_FIRST && (
              <Button type="link" onClick={() => onRecall(record.id)}>
                撤回
              </Button>
            )}
            {record.editable && status === ApprovalStatus.DRAFT && (
              <Button
                type="link"
                onClick={() =>
                  navigate(`/energy/usage/application/deferred/${record.id}?${urlSearchParams.toString()}`)
                }
              >
                编辑
              </Button>
            )}
            {record.editable && status === ApprovalStatus.REJECTED && (
              <Button type="link" onClick={() => onRelaunched(record.id)}>
                重新发起
              </Button>
            )}
            {record.editable && status === ApprovalStatus.PENDING_FIRST && (
              <Button type="link" onClick={() => onEncourageCompletion(record.id)}>
                催办
              </Button>
            )}
            {record.editable && (
              <PopConfirm
                placement="top"
                title="你确定要删除吗？"
                onConfirm={() => {
                  onHandleDelete(record.id);
                }}
              >
                <Button type="link">删除</Button>
              </PopConfirm>
            )}
          </Space>
        );
      },
    },
  ];

  const onRadioChange = (e: RadioChangeEvent) => {
    setGrade(e.target.value);
    updateFn();
  };
  const onHandleDelete = (v: number) => {
    apiV2EnergyConsumptionPlanDeleteDelayEnergyConsumptionPost({ id: v }).then(res => {
      updateFn();
    });
  };

  // 进入一键审批模式
  const enterBatchApprovalMode = () => {
    setIsBatchApprovalMode(true);
    setSelectedIds([]);
  };

  // 退出一键审批模式
  const exitBatchApprovalMode = () => {
    setIsBatchApprovalMode(false);
    setSelectedIds([]);
  };

  // 批量审批
  const handleBatchApproval = async (approvalResult: BatchApprovalStatus) => {
    if (selectedIds.length === 0) {
      message.warning('请先选择需要审批的申请');
      return;
    }

    Modal.confirm({
      title: `确定要批量${approvalResult === BatchApprovalStatus.APPROVED ? '通过' : '驳回'} ${
        selectedIds.length
      } 项延时申请吗？`,
      onOk: async () => {
        setBatchApprovalLoading(true);
        await apiV2EnergyConsumptionPlanOneKeyApprovalPost({
          ids: selectedIds,
          approvalType: approvalResult,
        })
          .then(() => {
            message.success('批量审批成功');
            setSelectedIds([]);
            updateFn();
          })
          .finally(() => setBatchApprovalLoading(false));
      },
    });
  };

  return (
    <div style={{ background: 'var(--mx-module-bg-color)', flex: 1 }}>
      <Space className={styles.dateSwitch} size={16}>
        <Space>
          <Checkbox value={check} onChange={e => setCheck(e.target.checked)}>
            我的待办
          </Checkbox>
        </Space>
        <Space>
          <Radio.Group onChange={onRadioChange} value={grade} buttonStyle="solid">
            <Radio.Button value={DelayTitleEnum.ALL}>全部({stateDatas?.totalNum ?? 0})</Radio.Button>
            <Radio.Button value={DelayTitleEnum.DRAFT}>草稿({stateDatas?.draftNum ?? 0})</Radio.Button>
            <Radio.Button value={DelayTitleEnum.PENDING}>待审批({stateDatas?.pendingApprovalNum ?? 0})</Radio.Button>
            <Radio.Button value={DelayTitleEnum.APPROVED}>已批准({stateDatas?.approvedNum ?? 0})</Radio.Button>
            <Radio.Button value={DelayTitleEnum.REJECTED}>已拒绝({stateDatas?.rejectNum ?? 0})</Radio.Button>
          </Radio.Group>
        </Space>
        <Space size={8}>
          {hasApplicationForDelay && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate(`/energy/usage/application/deferred?${urlSearchParams.toString()}`)}
            >
              延时用能申请
            </Button>
          )}
          {!isBatchApprovalMode ? (
            <Button type="primary" onClick={enterBatchApprovalMode}>
              批量审批
            </Button>
          ) : (
            <>
              <Button
                type="primary"
                onClick={() => handleBatchApproval(BatchApprovalStatus.APPROVED)}
                disabled={selectedIds.length === 0}
                loading={batchApprovalLoading}
              >
                批量通过
              </Button>
              <Button
                type="primary"
                danger
                onClick={() => handleBatchApproval(BatchApprovalStatus.REJECTED)}
                disabled={selectedIds.length === 0}
                loading={batchApprovalLoading}
              >
                批量驳回
              </Button>
              <Button onClick={exitBatchApprovalMode}>退出批量审批</Button>
            </>
          )}
        </Space>
      </Space>
      <Table
        loading={loading}
        rowKey="id"
        columns={buildColumns}
        dataSource={delayData}
        scroll={{ x: 1400 }}
        rowSelection={
          isBatchApprovalMode
            ? {
                selectedRowKeys: selectedIds,
                onChange: (keys: React.Key[]) => setSelectedIds(keys as number[]),
                getCheckboxProps: (record: DataItem) => ({
                  disabled:
                    !record.approvable ||
                    (record.approvalResult !== ApprovalStatus.PENDING_FIRST &&
                      record.approvalResult !== ApprovalStatus.PENDING_SECOND),
                }),
              }
            : undefined
        }
      />
      <Paging pagingInfo={pagingInfo} />
    </div>
  );
};

export default DelayApplication;
